/**
 * Which model is actually answering, read off the stream.
 *
 * `agents.model` names the model a run is switched to. The protocol frames say
 * which model served each turn, which trails a switch until that turn ends.
 * {@link useBlockManager} folds the readers below into `activeModel` and
 * stamps it onto each turn it flushes.
 *
 * Each reader answers only "what model does this frame name". Whether that
 * name is fit to show is `noteModel`'s call: it is the one funnel into
 * `activeModel`, so it is where CLI placeholders like `<synthetic>` are
 * dropped, whichever protocol offered them.
 */

import { isSubagentFrame } from '@reflex/shared';

/**
 * The model that produced a Claude `assistant` message, carried on the nested
 * Anthropic message as `model`. This is the per-turn ground truth: it names
 * the model that served the request, whatever the run was launched with.
 *
 * Subagent messages ({@link isSubagentFrame}) return null instead. A Task
 * subagent runs whatever model its agent definition pins — usually a smaller
 * one than the main thread — so reading its frames makes the chat claim the
 * session switched to Sonnet the moment the main Opus thread spawns an explore
 * agent.
 */
export function claudeAssistantModel(msg: Record<string, unknown>): string | null {
  if (isSubagentFrame(msg)) return null;
  const model = (msg.message as Record<string, unknown> | undefined)?.model;
  return typeof model === 'string' && model ? model : null;
}

/** A runtime switch as announced. */
export interface RuntimeSwitch {
  /** The model the switch puts in effect. */
  model: string;
  /**
   * Stream sequence of the post this announces; null for an announcement from
   * before the field shipped.
   */
  sequence: number | null;
}

/**
 * The switch an `agent.runtime_settings_changed` event announces. The only
 * reader that fires when a switch lands rather than when the next answer
 * arrives. Racing switches announce out of post order, so the sequence is
 * what {@link useBlockManager} orders them by.
 */
export function runtimeSettingsSwitch(data: Record<string, unknown>): RuntimeSwitch | null {
  const model = data.toModel;
  if (typeof model !== 'string' || !model) return null;
  const sequence = data.sequence;
  return {
    model,
    sequence: typeof sequence === 'number' && Number.isFinite(sequence) ? sequence : null,
  };
}
